import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { axiosInstance } from '../assets/utils/axiosInstance';
import { Language, LanguageState } from '../interfaces/Language';

export const fetchLanguageList = 
  createAsyncThunk<Language[], void, { rejectValue: string }>('/languages', 
    async (_, { rejectWithValue }) => {
      try {
        const response = await axiosInstance().get('/languages');
        return response.data;
      } catch (error) {
        // If an error occurs during the request, reject the thunk with an error message
        return rejectWithValue('Failed to fetch languages');
      }
    });

export const fetchLanguage =
  createAsyncThunk<{ type: string, data: Language }, { languageCode: string; type: 'translation' | 'localized' }, { rejectValue: string }>('/fetchLanguage', 
    async (arg, { rejectWithValue }) => {
      try {
        const response = await axiosInstance().get(`/languages?code=${arg.languageCode}`);
        return { type: arg.type, data: response.data[0] };
      } catch (error) {
        // If an error occurs during the request, reject the thunk with an error message
        return rejectWithValue('Failed to fetch language');
      }
    });

const initialState : LanguageState = {
  languageList: [],
  userTranslationLanguage: {
    id: 0,
    name: '',
    code: '',
  },
  userLocalizedLanguage: {
    id: 0,
    name: '',
    code: '',
  },
  languages: [],
  isLoading: false,
  error: null,
};

const languageSlice = createSlice({
  name: 'languages',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchLanguageList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLanguageList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.languageList = action.payload;
        state.error = null;
      })
      .addCase(fetchLanguageList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch languages';
      })
      .addCase(fetchLanguage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLanguage.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.type === 'translation') {
          state.userTranslationLanguage = action.payload.data;
        } else if (action.payload.type === 'localized') {
          state.userLocalizedLanguage = action.payload.data;
        }
        state.error = null;
      })
      .addCase(fetchLanguage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch language';
      });
  },
});

export default languageSlice.reducer;